import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { LangSwitch, PROV, provSetToken, StickyNav, useProvLang } from "./provShared";

const ERR_MAP = {
  INVALID_EMAIL: "err_email", NAME_REQUIRED: "err_required", ALREADY_REGISTERED: "err_already",
  CODE_INVALID: "err_code_invalid", CODE_EXPIRED: "err_code_expired", CODE_LOCKED: "err_code_locked",
  CODE_THROTTLE: "err_throttle", NO_ACCOUNT: "err_no_account",
  INVALID_CREDENTIALS: "err_invalid", LOCKED: "err_locked", WEAK_PASSWORD: "err_pw_short",
};

// PROVOCATIVE. START — sign in / register
// Default: e-mail + wachtwoord (login) en registratie met wachtwoord + bevestiging.
// De passwordless e-mailcode blijft beschikbaar als alternatief (bestaande wachtwoordloze members).
// "Forgot password" hergebruikt de bestaande site-resetflow (reset-link per e-mail, 1 uur geldig).
export default function ProvStart() {
  const nav = useNavigate();
  const { lang, setLang, t } = useProvLang();
  const [mode, setMode] = useState("login"); // login | register
  const [loginVia, setLoginVia] = useState("password"); // password (default) | code
  const [stage, setStage] = useState("form"); // form | code (code = verificatiecode invoeren)
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", gender: "" });
  const [pwField, setPwField] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [formErr, setFormErr] = useState(""); // formulier-brede melding
  const [resendIn, setResendIn] = useState(0);

  const errMsg = (field) => (errors[field] ? t(errors[field]) : null);

  const inputStyle = { color: PROV.primary };
  const inputCls = (field) =>
    `w-full bg-transparent border-b py-3 font-mono text-sm outline-none placeholder:text-[#4A4A4A] transition-colors ${
      errors[field] ? "border-[#FF6B5E]" : "border-[#333333] focus:border-[#BEBEBE]"}`;

  const startResendTimer = (sec) => {
    setResendIn(sec);
    const iv = setInterval(() => setResendIn((r) => (r <= 1 ? (clearInterval(iv), 0) : r - 1)), 1000);
  };

  const mapErr = (err) => ERR_MAP[err?.response?.data?.detail] || null;

  // e-mailcode aanvragen (alternatieve login voor wachtwoordloze members)
  const requestCode = async () => {
    const e = {};
    if (!f.email.trim()) e.email = "err_required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "err_email";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setFormErr("");
    try {
      await api.post("/provocative/auth/request-code", {
        mode: "login", email: f.email, first_name: "", last_name: "", gender: "", lang,
      });
      setStage("code");
      setCode("");
      startResendTimer(60);
    } catch (err) {
      const mapped = mapErr(err);
      if (mapped === "err_email") setErrors({ email: "err_email" });
      else setFormErr(mapped || "err_no_account");
    }
    setBusy(false);
  };

  const verifyCode = async () => {
    if (!code.trim()) { setFormErr("err_code_invalid"); return; }
    setBusy(true);
    setFormErr("");
    try {
      const { data } = await api.post("/provocative/auth/verify", { email: f.email, code: code.trim(), remember_me: remember });
      provSetToken(data.token, remember);
      nav("/provocative", { replace: true });
    } catch (err) {
      setFormErr(mapErr(err) || "err_code_invalid");
    }
    setBusy(false);
  };

  // wachtwoord-login (standaard)
  const pwLogin = async () => {
    const e = {};
    if (!f.email.trim()) e.email = "err_required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "err_email";
    if (!pwField) e.password = "err_required";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setFormErr("");
    try {
      const { data } = await api.post("/provocative/auth/password-login", { email: f.email, password: pwField, remember_me: remember });
      if (data.member_status === "none") { setFormErr("err_no_member"); setBusy(false); return; }
      provSetToken(data.token, remember);
      nav("/provocative", { replace: true });
    } catch (err) {
      setFormErr(err?.response?.status === 429 ? "err_locked" : "err_invalid");
    }
    setBusy(false);
  };

  // registratie met wachtwoord + bevestiging (account direct aangemaakt, status pending)
  const registerPassword = async () => {
    const e = {};
    if (!f.first_name.trim()) e.first_name = "err_required";
    if (!f.last_name.trim()) e.last_name = "err_required";
    if (!f.email.trim()) e.email = "err_required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "err_email";
    if (!pwField || pwField.length < 8) e.password = "err_pw_short";
    if (pwField !== pwConfirm) e.confirm = "err_pw_mismatch";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setFormErr("");
    try {
      const { data } = await api.post("/provocative/auth/register", {
        first_name: f.first_name, last_name: f.last_name, gender: f.gender,
        email: f.email, password: pwField, remember_me: remember,
      });
      provSetToken(data.token, remember);
      nav("/provocative", { replace: true });
    } catch (err) {
      if (err?.response?.status === 409) setErrors({ email: "err_email_taken" });
      else setFormErr(mapErr(err) || "err_required");
    }
    setBusy(false);
  };

  // wachtwoord vergeten — bestaande site-resetflow (link per e-mail; bevestigt altijd, lekt geen account-info)
  const forgotSubmit = async () => {
    const e = {};
    if (!f.email.trim()) e.email = "err_required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "err_email";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email: f.email }); } catch { /* altijd ok */ }
    setForgotSent(true);
    setBusy(false);
  };

  const fieldErr = (key, testid) =>
    errMsg(key) ? <p className="font-mono text-[11px] mt-1.5 text-[#FF6B5E]" data-testid={testid}>{errMsg(key)}</p> : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: PROV.bg, color: PROV.primary }} data-testid="prov-start">
      <StickyNav>
        <LangSwitch lang={lang} setLang={setLang} />
      </StickyNav>

      <main className="flex-1 flex flex-col items-center px-6 pt-14 md:pt-20 pb-16">
        <div className="w-full max-w-sm">
          {/* mode switch — verborgen in forgot-view */}
          {!forgot && stage === "form" && (
            <div className="grid grid-cols-2 mb-10 border" style={{ borderColor: PROV.border }} data-testid="prov-mode-switch">
              {[["login", t("sign_in")], ["register", t("register")]].map(([m, label]) => (
                <button key={m} onClick={() => { setMode(m); setErrors({}); setFormErr(""); }}
                  className={`py-3.5 font-mono text-[10px] tracking-[0.3em] uppercase transition-colors ${mode === m ? "bg-[#C1C1C1] text-[#0A0A0A]" : "hover:text-[#C1C1C1]"}`}
                  style={mode === m ? {} : { color: PROV.muted }}
                  data-testid={m === "login" ? "prov-mode-login" : "prov-mode-register"}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* ============ LOGIN — wachtwoord (standaard) ============ */}
          {stage === "form" && !forgot && mode === "login" && loginVia === "password" && (
            <>
              <div className="space-y-6">
                <div>
                  <input value={f.email} onChange={(e) => { setF({ ...f, email: e.target.value }); setErrors((p) => ({ ...p, email: null })); }}
                    placeholder={t("email")} type="email" autoComplete="email" inputMode="email" className={inputCls("email")} style={inputStyle} data-testid="prov-pw-email" />
                  {fieldErr("email", "prov-pw-email-error")}
                </div>
                <div>
                  <input value={pwField} onChange={(e) => { setPwField(e.target.value); setErrors((p) => ({ ...p, password: null })); }}
                    placeholder={t("password")} type="password" autoComplete="current-password" className={inputCls("password")} style={inputStyle}
                    onKeyDown={(e) => e.key === "Enter" && pwLogin()} data-testid="prov-pw-password" />
                  {fieldErr("password", "prov-pw-password-error")}
                </div>
              </div>
              <label className="flex items-center gap-3 mt-8 cursor-pointer" data-testid="prov-pw-remember-wrap">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-pw-remember" />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.secondary }}>{t("remember_me")}</span>
              </label>
              {formErr && <p className="font-mono text-[11px] mt-6 text-[#FF6B5E]" data-testid="prov-pw-error">{t(formErr)}</p>}
              <button onClick={pwLogin} disabled={busy}
                className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-pw-submit">
                {busy ? "…" : `${t("sign_in")} →`}
              </button>
              <div className="flex items-center justify-between mt-6">
                <button onClick={() => { setLoginVia("code"); setFormErr(""); setErrors({}); }}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
                  style={{ color: PROV.muted }} data-testid="prov-use-code">
                  {t("use_code")}
                </button>
                <button onClick={() => { setForgot(true); setForgotSent(false); setFormErr(""); setErrors({}); }}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
                  style={{ color: PROV.muted }} data-testid="prov-forgot-open">
                  {t("forgot")}
                </button>
              </div>
            </>
          )}

          {/* ============ LOGIN — e-mailcode (alternatief) ============ */}
          {stage === "form" && !forgot && mode === "login" && loginVia === "code" && (
            <>
              <div>
                <input value={f.email} onChange={(e) => { setF({ ...f, email: e.target.value }); setErrors((p) => ({ ...p, email: null })); }}
                  placeholder={t("email")} type="email" autoComplete="email" inputMode="email" className={inputCls("email")} style={inputStyle} data-testid="prov-email" />
                {fieldErr("email", "prov-email-error")}
              </div>
              <label className="flex items-center gap-3 mt-8 cursor-pointer" data-testid="prov-remember-wrap">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-remember" />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.secondary }}>{t("remember_me")}</span>
              </label>
              {formErr && <p className="font-mono text-[11px] mt-6 text-[#FF6B5E]" data-testid="prov-form-error">{t(formErr)}</p>}
              <button onClick={requestCode} disabled={busy}
                className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-send-code">
                {busy ? "…" : t("send_code")}
              </button>
              <button onClick={() => { setLoginVia("password"); setFormErr(""); setErrors({}); }}
                className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
                style={{ color: PROV.muted }} data-testid="prov-use-password">
                {t("use_password")}
              </button>
            </>
          )}

          {/* ============ REGISTER — wachtwoord + bevestiging ============ */}
          {stage === "form" && !forgot && mode === "register" && (
            <>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input value={f.first_name} onChange={(e) => { setF({ ...f, first_name: e.target.value }); setErrors((p) => ({ ...p, first_name: null })); }}
                      placeholder={t("first_name")} autoComplete="given-name" className={inputCls("first_name")} style={inputStyle} data-testid="prov-reg-first" />
                    {fieldErr("first_name", "prov-reg-first-error")}
                  </div>
                  <div>
                    <input value={f.last_name} onChange={(e) => { setF({ ...f, last_name: e.target.value }); setErrors((p) => ({ ...p, last_name: null })); }}
                      placeholder={t("last_name")} autoComplete="family-name" className={inputCls("last_name")} style={inputStyle} data-testid="prov-reg-last" />
                    {fieldErr("last_name", "prov-reg-last-error")}
                  </div>
                </div>
                <div>
                  <select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}
                    className={`w-full bg-transparent border-b py-3 font-mono text-sm outline-none transition-colors ${f.gender ? "border-[#333333]" : "border-[#333333]"}`}
                    style={{ color: f.gender ? PROV.primary : "#4A4A4A", background: PROV.bg }} data-testid="prov-reg-gender">
                    <option value="">{t("gender")}</option>
                    <option value="female">{t("gender_female")}</option>
                    <option value="male">{t("gender_male")}</option>
                    <option value="undisclosed">{t("gender_undisclosed")}</option>
                  </select>
                </div>
                <div>
                  <input value={f.email} onChange={(e) => { setF({ ...f, email: e.target.value }); setErrors((p) => ({ ...p, email: null })); }}
                    placeholder={t("email")} type="email" autoComplete="email" inputMode="email" className={inputCls("email")} style={inputStyle} data-testid="prov-reg-email" />
                  {fieldErr("email", "prov-reg-email-error")}
                </div>
                <div>
                  <input value={pwField} onChange={(e) => { setPwField(e.target.value); setErrors((p) => ({ ...p, password: null })); }}
                    placeholder={t("password")} type="password" autoComplete="new-password" className={inputCls("password")} style={inputStyle} data-testid="prov-reg-password" />
                  {fieldErr("password", "prov-reg-password-error")}
                </div>
                <div>
                  <input value={pwConfirm} onChange={(e) => { setPwConfirm(e.target.value); setErrors((p) => ({ ...p, confirm: null })); }}
                    placeholder={t("confirm_password")} type="password" autoComplete="new-password" className={inputCls("confirm")} style={inputStyle}
                    onKeyDown={(e) => e.key === "Enter" && registerPassword()} data-testid="prov-reg-confirm" />
                  {fieldErr("confirm", "prov-reg-confirm-error")}
                </div>
              </div>
              <label className="flex items-center gap-3 mt-8 cursor-pointer" data-testid="prov-reg-remember-wrap">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-reg-remember" />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.secondary }}>{t("remember_me")}</span>
              </label>
              {formErr && <p className="font-mono text-[11px] mt-6 text-[#FF6B5E]" data-testid="prov-reg-error">{t(formErr)}</p>}
              <button onClick={registerPassword} disabled={busy}
                className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-reg-submit">
                {busy ? "…" : `${t("register")} →`}
              </button>
            </>
          )}

          {/* ============ FORGOT PASSWORD ============ */}
          {stage === "form" && forgot && (
            <>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase mb-8" style={{ color: PROV.muted }} data-testid="prov-forgot-title">{t("forgot")}</div>
              {forgotSent ? (
                <p className="font-mono text-xs leading-relaxed" style={{ color: PROV.secondary }} data-testid="prov-forgot-sent">{t("forgot_sent")}</p>
              ) : (
                <>
                  <div>
                    <input value={f.email} onChange={(e) => { setF({ ...f, email: e.target.value }); setErrors((p) => ({ ...p, email: null })); }}
                      placeholder={t("email")} type="email" autoComplete="email" inputMode="email" className={inputCls("email")} style={inputStyle} data-testid="prov-forgot-email" />
                    {fieldErr("email", "prov-forgot-email-error")}
                  </div>
                  <button onClick={forgotSubmit} disabled={busy}
                    className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                    style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-forgot-submit">
                    {busy ? "…" : t("send_reset")}
                  </button>
                </>
              )}
              <button onClick={() => { setForgot(false); setFormErr(""); setErrors({}); }}
                className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
                style={{ color: PROV.muted }} data-testid="prov-forgot-back">
                {t("back")}
              </button>
            </>
          )}

          {/* ============ CODE VERIFICATIE ============ */}
          {stage === "code" && (
            <>
              <p className="font-mono text-xs leading-relaxed" style={{ color: PROV.secondary }} data-testid="prov-code-hint">
                {t("check_email")}
                <br />{t("code_hint")}
              </p>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder={t("code_placeholder")} inputMode="numeric" autoComplete="one-time-code"
                className={`mt-8 w-full bg-transparent border-b py-3 font-mono text-2xl tracking-[0.5em] outline-none transition-colors text-center placeholder:text-[#333333] ${
                  formErr ? "border-[#FF6B5E]" : "border-[#333333] focus:border-[#BEBEBE]"}`}
                style={inputStyle} data-testid="prov-code" />
              <label className="flex items-center gap-3 mt-8 cursor-pointer" data-testid="prov-remember-code-wrap">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-remember-code" />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.secondary }}>{t("remember_me")}</span>
              </label>
              {formErr && <p className="font-mono text-[11px] mt-6 text-[#FF6B5E]" data-testid="prov-code-error">{t(formErr)}</p>}
              <button onClick={verifyCode} disabled={busy}
                className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-verify">
                {busy ? "…" : t("verify")}
              </button>
              <div className="flex items-center justify-between mt-6">
                <button onClick={() => { setStage("form"); setFormErr(""); setCode(""); }}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
                  style={{ color: PROV.muted }} data-testid="prov-back">
                  {t("back")}
                </button>
                <button onClick={requestCode} disabled={busy || resendIn > 0}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1] disabled:opacity-40"
                  style={{ color: PROV.muted }} data-testid="prov-resend">
                  {resendIn > 0 ? `${t("resend_in")} ${resendIn}S` : t("resend")}
                </button>
              </div>
            </>
          )}
          {/* admin-ingang: naar de aparte admin-login op /provocative/admin */}
          <div className="mt-16 text-center">
            <button onClick={() => nav("/provocative/admin")}
              className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
              style={{ color: PROV.muted }} data-testid="prov-admin-link">
              ADMIN →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
